import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../context/UserContext";
import {
  FaCreditCard,
  FaCrown,
  FaGem,
  FaUserCog,
  FaKey,
  FaGlobe,
  FaLock,
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaBolt,
  FaStar,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Layout from "../components/Layout";

// Aceeași funcție ca în Header
const getTierDisplay = (tier) => {
  const tiers = {
    trial: {
      name: "Free Trial",
      color: "text-yellow-400",
      bg: "bg-yellow-500/20",
      icon: <FaClock className="text-yellow-400 text-lg" />,
      border: "border-yellow-500/30",
    },
    basic: {
      name: "Basic Member",
      color: "text-blue-400",
      bg: "bg-blue-500/20",
      icon: <FaBolt className="text-blue-400 text-lg" />,
      border: "border-blue-500/30",
    },
    pro: {
      name: "Pro Member",
      color: "text-green-400",
      bg: "bg-green-500/20",
      icon: <FaStar className="text-green-400 text-lg" />,
      border: "border-green-500/30",
    },
    premium: {
      name: "Premium Member",
      color: "text-purple-400",
      bg: "bg-purple-500/20",
      icon: <FaCrown className="text-yellow-400 text-lg" />,
      border: "border-purple-500/30",
    },
  };
  return (
    tiers[tier] || {
      name: tier,
      color: "text-gray-400",
      bg: "bg-gray-500/20",
      icon: null,
      border: "border-gray-500/30",
    }
  );
};

const Profile = () => {
  const { user, setUser, language, setLanguage, saveLanguageToBackend } =
    useContext(UserContext);
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState({
    tier: "free",
    status: "active",
    start_date: new Date().toISOString(),
    end_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
  });
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Verifică dacă utilizatorul este VisionRoyale
  const isVisionRoyale = user?.username === "VisionRoyale";

  // State pentru setări
  const [email, setEmail] = useState(user?.email || "");
  const [localLanguage, setLocalLanguage] = useState(language || "en");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [initialSettings, setInitialSettings] = useState({
    email: user?.email || "",
    language: language || "en",
  });

  // Obține numele afișat al abonamentului folosind tier-ul
  const subscriptionDisplay = getTierDisplay(subscription.tier);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const subResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/subscriptions/status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSubscription({
          tier: subResponse.data.tier, // Folosim tier, nu type
          status: subResponse.data.status,
          start_date: subResponse.data.start_date || new Date().toISOString(),
          end_date:
            subResponse.data.end_date ||
            new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        });
        setDaysRemaining(subResponse.data.days_remaining);
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setLocalLanguage(language || "en");
      setInitialSettings({
        email: user.email || "",
        language: language || "en",
      });
    }
  }, [user, language]);

  useEffect(() => {
    const calculateDays = () => {
      if (subscription.end_date) {
        const endDate = new Date(subscription.end_date);
        const today = new Date();
        const days = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        setDaysRemaining(days > 0 ? days : 0);
        if (days <= 0 && subscription.status !== "expired") {
          setSubscription((prev) => ({ ...prev, status: "expired" }));
        }
      }
    };
    calculateDays();
    const interval = setInterval(calculateDays, 86400000);
    return () => clearInterval(interval);
  }, [subscription]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "N/A";
      }
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(
        language === "ro" ? "ro-RO" : "en-US",
        options,
      );
    } catch (e) {
      console.error("Error formatting date:", e);
      return "N/A";
    }
  };

  const haveSettingsChanged = () => {
    return (
      email !== initialSettings.email ||
      localLanguage !== initialSettings.language ||
      newPassword !== "" ||
      currentPassword !== ""
    );
  };

  const handleSaveSettings = async () => {
    // Blochează pentru VisionRoyale
    if (isVisionRoyale) {
      setError("Settings cannot be changed for Test User VisionRoyale.");
      return;
    }

    if (!haveSettingsChanged()) {
      return;
    }
    setIsSaving(true);
    setError("");
    if (newPassword && newPassword !== confirmPassword) {
      setError("New passwords don't match");
      setIsSaving(false);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Not authenticated");
        return;
      }
      // Dacă limba s-a schimbat
      if (localLanguage !== initialSettings.language) {
        // Salvează în backend
        if (saveLanguageToBackend) {
          await saveLanguageToBackend(localLanguage);
        }
        // Actualizează în context
        setLanguage(localLanguage);
      }
      // Salvează toate setările
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/user/settings`,
        {
          email,
          currentPassword: currentPassword || undefined,
          newPassword: newPassword || undefined,
          language: localLanguage,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(response.data.user);
      // Actualizează valorile inițiale
      setInitialSettings({
        email: response.data.user.email || email,
        language: response.data.language || localLanguage,
      });
      setSuccess("Settings saved successfully!");
      // Resetează câmpurile de parolă
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error(
        "Error saving settings:",
        err.response?.data || err.message,
      );
      setError(
        err.response?.data?.message ||
          "Failed to save settings. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleLanguageSelect = (lang) => {
    if (!isVisionRoyale) {
      setLocalLanguage(lang);
    }
  };

  const languageOptions = [
    { code: "en", label: "English", flag: "🇬🇧" },
    // { code: "de", label: "German", flag: "🇩🇪" },
    // { code: "ro", label: "Romanian", flag: "🇷🇴" },
  ];

  const Notification = () => {
    if (!error && !success) return null;
    return (
      <motion.div
        className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
      >
        <div
          className={`px-6 py-3 rounded-lg shadow-lg backdrop-blur-xl border ${
            error
              ? "bg-red-900/30 border-red-500/50"
              : "bg-green-900/30 border-green-500/50"
          }`}
        >
          <p
            className={`font-medium ${
              error ? "text-red-300" : "text-green-300"
            }`}
          >
            {error || success}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <Layout>
      <Notification />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 p-4 md:p-6">
        <div className="max-w-5xl mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Profile Settings
            </h1>
            <p className="text-gray-400 mt-2">
              Manage your account and preferences
            </p>
          </motion.div>

          {/* Card 1: Subscription */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-6 md:p-8 mb-6 backdrop-blur-xl"
          >
            <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.2)] pointer-events-none"></div>

            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
              <div className="flex items-center gap-3 mb-4 md:mb-0">
                <FaCreditCard className="text-green-400 text-2xl" />
                <h2 className="text-2xl font-bold text-green-300">
                  Subscription
                </h2>
              </div>
              <div className="bg-black/30 px-4 py-2 rounded-full flex items-center border border-green-500/30">
                <FaClock className="text-green-400 mr-2" />
                <span className="text-white font-bold">
                  {Math.floor(daysRemaining)} days remaining
                </span>
              </div>
            </div>

            {/* Subscription Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20 text-center">
                <div className="flex items-center justify-center mb-2">
                  <FaCalendarAlt className="text-green-400 mr-2" />
                  <h4 className="text-gray-400 text-sm">Start Date</h4>
                </div>
                <p className="text-xl text-white font-bold">
                  {formatDate(subscription.start_date)}
                </p>
              </div>

              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20 text-center">
                <div className="flex items-center justify-center mb-2">
                  <FaCalendarAlt className="text-green-400 mr-2" />
                  <h4 className="text-gray-400 text-sm">End Date</h4>
                </div>
                <p className="text-xl text-white font-bold">
                  {formatDate(subscription.end_date)}
                </p>
              </div>

              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20 text-center">
                <div className="flex items-center justify-center mb-2">
                  <FaCrown className="text-green-400 mr-2" />
                  <h4 className="text-gray-400 text-sm">Member Type</h4>
                </div>
                <p
                  className={`text-xl text-white font-bold flex items-center justify-center ${subscriptionDisplay.color}`}
                >
                  {subscriptionDisplay.icon}
                  <span className="ml-2">{subscriptionDisplay.name}</span>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Account Settings - păstrăm la fel ca înainte */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative bg-gray-900/80 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/30 p-6 md:p-8 backdrop-blur-xl"
          >
            <div className="absolute inset-0 rounded-2xl border border-green-500/20 shadow-[0_0_40px_rgba(34,197,94,0.2)] pointer-events-none"></div>

            <div className="flex items-center gap-3 mb-6">
              <FaUserCog className="text-green-400 text-2xl" />
              <h2 className="text-2xl font-bold text-green-300">
                Account Settings
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaUser className="mr-2 text-green-400" />
                  Personal Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">
                      Username
                    </label>
                    <div
                      className={`bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 ${isVisionRoyale ? "cursor-not-allowed opacity-80" : ""}`}
                    >
                      {user?.username || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">
                      Email Address
                    </label>
                    {isVisionRoyale ? (
                      <div className="bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 cursor-not-allowed opacity-80">
                        {email || "N/A"}
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Language Preferences */}
              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaGlobe className="mr-2 text-green-400" />
                  Language Preferences
                </h3>
                <div className="flex flex-col gap-3">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      disabled={isVisionRoyale}
                      className={`px-4 py-3 rounded-lg flex items-center transition-all ${
                        localLanguage === lang.code
                          ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-[0_0_15px_rgba(34,197,94,0.6)]"
                          : "bg-gray-800/60 text-gray-300 hover:bg-gray-700/60"
                      } ${isVisionRoyale ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <span className="mr-2 text-xl">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div className="mt-6">
              <div className="bg-black/30 p-4 rounded-lg border border-green-500/20">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <FaKey className="mr-2 text-green-400" />
                  Security
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">
                      Current Password
                    </label>
                    {isVisionRoyale ? (
                      <div className="bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 cursor-not-allowed opacity-80">
                        ••••••••
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-1 text-sm">
                      New Password
                    </label>
                    {isVisionRoyale ? (
                      <div className="bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 cursor-not-allowed opacity-80">
                        ••••••••
                      </div>
                    ) : (
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                      />
                    )}
                  </div>
                  {!isVisionRoyale && (
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-gray-800/60 text-white p-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-green-400 focus:border-green-400 transition"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Save Button - doar pentru utilizatori normali */}
            {!isVisionRoyale && (
              <div className="flex justify-end mt-6">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSaving || !haveSettingsChanged()}
                  className={`bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.6)] hover:from-green-600 hover:to-green-700 transition-all duration-200 ${
                    isSaving || !haveSettingsChanged()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            )}

            {/* Mesaj pentru VisionRoyale */}
            {isVisionRoyale && (
              <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4 mt-6">
                <p className="text-yellow-400 text-sm text-center">
                  ⚠️ Account settings are in view-only. No changes are allowed
                  for Test User VisionRoyale.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
